/**
 * frontend/src/hooks/usePermissions.js
 *
 * Two hooks:
 *
 * usePageAccess()
 *   Returns { allowedPaths: Set<string>, loading: bool }
 *   Used by Layout.jsx to filter the sidebar and by the route guard.
 *   Fetches once per session and caches in module scope.
 *
 * useProjectPermissions(projectId)
 *   Returns { permissions: Record<string, bool>, loading: bool, can: (code) => bool }
 *   Used inside any project page to gate UI elements.
 *   Example: const { can } = useProjectPermissions(id);
 *            {can('advance_stage') && <button>Advance Stage</button>}
 */
import { useState, useEffect, useRef } from 'react';
import { api } from '../utils/api';

// Module-level cache — persists across component remounts within the session
// Keyed by userId so switching accounts always refetches
const pageCache   = { userId: null, paths: null, perms: null, promise: null };
const projCache   = {};   // { projectId: { permissions, timestamp } }
const PROJ_TTL_MS = 60000; // 60 seconds — re-fetch if stale

// ── usePageAccess ──────────────────────────────────────────────────────────
export function usePageAccess(userId) {
  const [allowedPaths, setAllowedPaths] = useState(null);
  const [pagePerms,    setPagePerms]    = useState({});
  const [loading,      setLoading]      = useState(true);

  useEffect(() => {
    if (!userId) { setLoading(false); return; }

    // Always re-fetch — the cache only helps within a single page navigation session
    // Clear any stale cache so every login gets fresh data from the DB
    if (pageCache.userId !== userId) {
      pageCache.paths   = null;
      pageCache.perms   = null;
      pageCache.promise = null;
    }
    // Only use cache if same session AND has meaningful data
    if (pageCache.userId === userId && pageCache.paths && pageCache.paths.size > 3) {
      setAllowedPaths(pageCache.paths);
      setPagePerms(pageCache.perms || {});
      setLoading(false);
      return;
    }
    pageCache.paths   = null;
    pageCache.perms   = null;
    pageCache.promise = null;

    // Deduplicate concurrent fetches
    if (pageCache.userId === userId && pageCache.promise) {
      pageCache.promise.then(({ paths, perms }) => {
        setAllowedPaths(paths);
        setPagePerms(perms || {});
        setLoading(false);
      });
      return;
    }

    // Reset cache for new user
    pageCache.userId = userId;
    pageCache.promise = api.get('/page-access/my-pages').then(r => {
      const data  = r.data || [];
      const paths = new Set(data.map(p => p.path));
      // Always include root paths
      paths.add('/login');
      paths.add('/404');
      // Build R/W/U permission map keyed by path
      const perms = {};
      data.forEach(p => {
        perms[p.path] = {
          read:   p.can_read   !== false,   // default true
          write:  p.can_write  === true,    // default false
          update: p.can_update === true,    // default false
        };
      });
      pageCache.paths   = paths;
      pageCache.perms   = perms;
      pageCache.promise = null;
      return { paths, perms };
    }).catch(() => {
      // On error: fail closed — only allow core always-visible paths
      const paths = new Set(['/login', '/404', '/']);
      const perms = {};
      pageCache.paths   = paths;
      pageCache.perms   = perms;
      pageCache.promise = null;
      return { paths, perms };
    });

    pageCache.promise.then(({ paths, perms }) => {
      setAllowedPaths(paths);
      setPagePerms(perms);
      setLoading(false);
    });
  }, [userId]);

  return { allowedPaths, pagePerms, loading };
}

// Clear the page access cache (call on logout or role change)
export function clearPageAccessCache() {
  pageCache.userId = null; // force next call to re-fetch
  pageCache.userId  = null;
  pageCache.paths   = null;
  pageCache.promise = null;
}

// ── useProjectPermissions ──────────────────────────────────────────────────
export function useProjectPermissions(projectId) {
  const [permissions, setPermissions] = useState({});
  const [loading,     setLoading]     = useState(true);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    if (!projectId) { setLoading(false); return; }

    // Cache hit
    const cached = projCache[projectId];
    if (cached && (Date.now() - cached.timestamp < PROJ_TTL_MS)) {
      setPermissions(cached.permissions);
      setLoading(false);
      return;
    }

    setLoading(true);
    api.get('/project-roles/' + projectId + '/my-permissions')
      .then(r => {
        if (!mounted.current) return;
        const perms = r.data || {};
        projCache[projectId] = { permissions: perms, timestamp: Date.now() };
        setPermissions(perms);
      })
      .catch(() => {
        // On error, return empty (deny by default — safer than fail-open for project actions)
        if (mounted.current) setPermissions({});
      })
      .finally(() => {
        if (mounted.current) setLoading(false);
      });

    return () => { mounted.current = false; };
  }, [projectId]);

  return {
    permissions,
    loading,
    // Convenience: can('approve_gates') instead of permissions.approve_gates
    can: (code) => permissions[code] === true,
  };
}

// Invalidate a project's permission cache (call after role assignment changes)
export function invalidateProjectPermissions(projectId) {
  delete projCache[projectId];
}
